import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Notify } from 'notiflix';
import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Typography,
  Chip,
  Tabs,
  Tab,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { useTierManagement } from 'hooks/useTierManagement';
import TierCard from 'examples/ValuePack/components/TierCard';
import FeatureTable from 'examples/ValuePack/components/FeatureTable';
import { validateTiers } from 'schemas/valuePackSchema';
import {
  selectAllCategoriesWithFeatures,
  selectCurrentValuePack,
  fetchValuePackDetails,
  fetchCategoriesOptimized,
} from 'store/slices/valuePackSlice';
import { DiscountTypes } from 'config/valuePackFormConfig';
import { useDuration } from 'context/DurationContext';
import { useCountry } from 'context/CountryContext';
import CountryDropdown from 'components/country/CountryDropdown';
import DurationDropdown from 'components/Duration/DurationDropdown';

const EditValuePackTier = () => {
  const { valuePackId, mode, durationId, countryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isCreateMode = mode === 'new' || (!durationId && !countryId);
  const isEditMode = !isCreateMode;

  const { 
    durations, 
    loading: durationLoading, 
    error: durationError
  } = useDuration();
  
  const { 
    countries, 
    loading: countryLoading,
    error: countryError
  } = useCountry();

  const [activeTab, setActiveTab] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  const dispatch = useDispatch();
  const storedValuePack = useSelector(selectCurrentValuePack);
  const allCategoriesWithFeatures = useSelector(selectAllCategoriesWithFeatures);

  // The list only carries minimal rows, so the pack (tiers, categories) and the
  // category catalog are loaded on demand when this screen opens.
  const currentValuePack =
    storedValuePack && String(storedValuePack.id) === String(valuePackId) ? storedValuePack : null;

  useEffect(() => {
    if (valuePackId && !currentValuePack) {
      dispatch(fetchValuePackDetails(valuePackId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, valuePackId]);

  useEffect(() => {
    if (!allCategoriesWithFeatures.length) {
      dispatch(fetchCategoriesOptimized());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // MODIFIED: Pass durationId and countryId only in edit mode
  const {
    loading: tierLoading,
    // isEditMode, // Remove this if not needed from hook
    selectedDuration,
    selectedCountry,
    tierData,
    handleDurationChange,
    handleCountryChange,
    handleTierFieldChange,
    handleFeatureToggle,
    handleSave,
    calculateCurrentPrice
  } = useTierManagement(isEditMode ? { durationId, countryId } : {});

  const getSelectedDurationObject = () => {
    if (!selectedDuration) return null;
    return durations.find(d => d.id === selectedDuration);
  };

  const getSelectedCountryObject = () => {
    if (!selectedCountry) return null;
    return countries.find(c => c.id === selectedCountry);
  };

  const selectedCountryData = getSelectedCountryObject();
  const selectedDurationData = getSelectedDurationObject();

  const getCategoriesWithFeatures = () => {    
    if (!currentValuePack || !Array.isArray(currentValuePack.categories)) return [];

    const categoryIds = currentValuePack.categories.map(category => {
      return typeof category === 'object' ? category.id : category;
    });
    
    const categories = categoryIds
      .map(categoryId => {
        const foundCategory = allCategoriesWithFeatures.find(cat => cat.id === categoryId);
        if (!foundCategory) {
          console.warn(`Category not found with ID: ${categoryId}`);
        }
        return foundCategory;
      })
      .filter(Boolean);

    return categories;
  };

  const categoriesWithFeatures = getCategoriesWithFeatures();

  const validateTierData = async () => {
    try {
      const tiersArray = Object.values(tierData).filter(tier => 
        tier && (tier.originalPrice > 0 || tier.categoryFeatures?.length > 0 || tier.additionalFeatures?.length > 0)
      );
      
      const validTiers = tiersArray.map(tier => ({
        ...tier,
        durationId: selectedDuration,
        countryId: selectedCountry
      }));

      await validateTiers({ tiers: validTiers });
      setValidationErrors({});
      return true;
    } catch (validationError) {
      const errors = {};
      if (validationError.inner) {
        validationError.inner.forEach(error => {
          errors[error.path] = error.message;
        });
      }
      setValidationErrors(errors);
      
      if (validationError.inner && validationError.inner[0]) {
        Notify.failure(validationError.inner[0].message);
      }
      
      return false;
    }
  };

  const handleSaveWithValidation = async () => {
    const isValid = await validateTierData();
    if (isValid) {
      await handleSave();
      navigate(`/value-pack/${valuePackId}/tiers`);
    }
  };

  const loading = tierLoading || durationLoading || countryLoading;

  // Show loading only if we have params
  if (loading && (isEditMode ? (durationId && countryId) : true)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
          <Typography ml={2}>Loading tier configuration...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  const getDurationLabel = (duration) => {
    if (!duration) return '';
    
    if (duration.displayLabel) return duration.displayLabel;
    if (duration.label) return duration.label;
    
    const value = duration.value || 0;
    const unit = duration.unit || 'month';
    
    const unitLabels = {
      day: 'Day',
      week: 'Week',
      month: 'Month',
      quarter: 'Quarter',
      biannual: '6 Months',
      year: 'Year',
      custom: 'Custom',
    };
    
    const unitLabel = unitLabels[unit] || unit;
    const plural = value > 1 ? 's' : '';
    
    return `${value} ${unitLabel}${plural}`;
  };

  const calculateFinalPrice = (tier) => {
    if (!tier) return 0;
    
    if (tier.discountType === DiscountTypes.FLAT) {
      return Math.max(0, tier.originalPrice - tier.flatDiscount);
    } else {
      const discountAmount = (tier.originalPrice * tier.percentageDiscount) / 100;
      return Math.max(0, tier.originalPrice - discountAmount);
    }
  };

  const calculatePricePerMonth = (tier) => {
    const finalPrice = calculateFinalPrice(tier);
    const durationValue = selectedDurationData?.value || 1;
    
    if (!finalPrice || !durationValue || durationValue === 0) return 0;
    
    let monthsValue = durationValue;
    if (selectedDurationData?.unit === 'year') {
      monthsValue = durationValue * 12;
    } else if (selectedDurationData?.unit === 'quarter') {
      monthsValue = durationValue * 3;
    } else if (selectedDurationData?.unit === 'biannual') {
      monthsValue = durationValue * 6;
    }
    
    return Math.round(finalPrice / monthsValue);
  };

  const formatPrice = (price) => {
    return Math.round(price).toLocaleString();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate(`/value-pack/${valuePackId}/tiers`)}
                sx={{ color: '#fff', mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              {/* Dynamic title based on mode */}
              <MDTypography variant="h6" color="white">
                {currentValuePack?.name} - {isEditMode ? 'Edit Tiers' : 'Create New Tiers'}
              </MDTypography>
            </Box>
            
            <Button
              variant="contained"
              color="light"
              onClick={handleSaveWithValidation}
              startIcon={<SaveIcon />}
              disabled={!selectedDuration || !selectedCountry}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                }
              }}
            >
              {/* Dynamic button text */}
              {isEditMode ? 'Update Tiers' : 'Create Tiers'}
            </Button>
          </MDBox>

          <MDBox p={3}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ mb: 3 }}
            >
              <Tab label="Duration & Country" />
              <Tab label="Configure Tiers" />
              <Tab label="Features Mapping" />
              <Tab label="Preview" />
            </Tabs>

            {Object.keys(validationErrors).length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>Validation Errors</AlertTitle>
                Please fix the validation errors before saving.
              </Alert>
            )}

            {/* Tab 1: Duration & Country */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity={isEditMode ? "warning" : "info"} sx={{ mb: 3 }}>
                    {isEditMode 
                      ? `You are editing existing tiers for ${selectedDurationData ? getDurationLabel(selectedDurationData) : ''} in ${selectedCountryData?.countryName || ''}.`
                      : "Select duration and country first, then configure pricing tiers for that specific combination."
                    }
                  </Alert>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Duration
                    </Typography>
                    
                    <DurationDropdown
                      value={selectedDuration || ''}
                      onChange={handleDurationChange}
                      label="Duration"
                      required={true}
                      showActiveOnly={true}
                      showDefaultFirst={true}
                      fullWidth={true}
                      size="large"
                      variant="outlined"
                      helperText="Subscription duration"
                      error={!!validationErrors.duration}
                      disabled={isEditMode} 
                    />
                    
                    {selectedDurationData && (
                      <Typography variant="body2" color="success.main" mt={2}>
                        ✓ {getDurationLabel(selectedDurationData)}
                      </Typography>
                    )}
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Country
                    </Typography>
                    
                    <CountryDropdown
                      value={selectedCountry || ''}
                      onChange={(value) => handleCountryChange(value)}
                      label="Country"
                      required={true}
                      showActiveOnly={true}
                      fullWidth={true}
                      size="large"
                      variant="outlined"
                      helperText="Country"
                      showSearch={true}
                      showRegionFilter={true}
                      includeFlag={true}
                      error={!!validationErrors.country}
                      disabled={isEditMode} // ✅ Disable in edit mode
                    />
                    
                    {selectedCountryData && (
                      <Typography variant="body2" color="success.main" mt={2}>
                        ✓ {selectedCountryData?.name} ({selectedCountryData?.currencyCode || 'USD'})
                      </Typography>
                    )}
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Tab 2: Configure Tiers */}
            {activeTab === 1 && selectedDuration && selectedCountry && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>{isEditMode ? 'Editing' : 'Configuring'}:</strong> {selectedDurationData ? getDurationLabel(selectedDurationData) : ''} • {selectedCountryData?.name || ''}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Configure Low, Mid, and High tier pricing
                    </Typography>
                  </Alert>
                </Grid>

                {['Low', 'Mid', 'High'].map(tierType => (
                  <Grid item xs={12} md={4} key={tierType}>
                    <TierCard
                      tierType={tierType}
                      tierData={tierData?.[tierType] || {}}
                      onFieldChange={(field, value) => handleTierFieldChange(tierType, field, value)}
                      countryCurrency={selectedCountryData?.currencyCode || 'USD'}
                      color={
                        tierType === 'Low' ? 'info.main' : 
                        tierType === 'Mid' ? 'warning.main' : 'success.main'
                      }
                      errors={validationErrors}
                      duration={selectedDurationData?.value || 1}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Tab 3: Features Mapping */}
            {activeTab === 2 && selectedDuration && selectedCountry && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Feature Configuration
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Configure which features are available in each tier for {selectedDurationData ? getDurationLabel(selectedDurationData) : ''} in {selectedCountryData?.name || ''}
                  </Typography>
                </Grid>

                {categoriesWithFeatures.map(category => {
                  if (!category) return null;

                  return (
                    <Grid item xs={12} key={category.id}>
                      <Card>
                        <MDBox p={3}>
                          <Typography variant="h6" gutterBottom>
                            {category.name} - Features
                          </Typography>

                          <FeatureTable
                            features={category.features.map(f => ({ ...f, categoryId: category.id }))}
                            selectedFeatures={tierData}
                            onFeatureToggle={handleFeatureToggle}
                            title="Available Features"
                            featureKey="id"
                            featureNameKey="name"
                            featureType="category"
                          />
                        </MDBox>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* Tab 4: Preview */}
            {activeTab === 3 && selectedDuration && selectedCountry && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Tiers Preview
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Preview of all tiers for {selectedDurationData ? getDurationLabel(selectedDurationData) : ''} in {selectedCountryData?.name || ''}
                  </Typography>
                </Grid>
              
                {['Low', 'Mid', 'High'].map(tierType => {
                  const tier = tierData?.[tierType];
                  if (!tier) return null;
                  
                  const finalPrice = calculateFinalPrice(tier);
                  const pricePerMonth = calculatePricePerMonth(tier);

                  return (
                    <Grid item xs={12} md={4} key={tierType}>
                      <Card>
                        <MDBox p={3}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" color={
                              tierType === 'Low' ? 'info.main' : 
                              tierType === 'Mid' ? 'warning.main' : 'success.main'
                            }>
                              {tier.name || `${tierType} Tier`}
                            </Typography>
                            {tier.isPopular && (
                              <Chip label="Popular" color="secondary" size="small" />
                            )}
                          </Box>
                          
                          <Typography variant="body2" color="textSecondary" mt={1}>
                            {tier.description || 'No description'}
                          </Typography>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="h5" color="primary" gutterBottom>
                            {selectedCountryData?.currencyCode || 'USD'} {formatPrice(finalPrice)}
                            {(tier.discountType === DiscountTypes.FLAT && tier.flatDiscount > 0) || 
                            (tier.discountType === DiscountTypes.PERCENTAGE && tier.percentageDiscount > 0) ? (
                              <Typography component="span" variant="body2" sx={{ textDecoration: 'line-through', ml: 1, color: 'text.secondary' }}>
                                {selectedCountryData?.currencyCode || 'USD'} {formatPrice(tier.originalPrice)}
                              </Typography>
                            ) : null}
                          </Typography>
                          
                          <Typography variant="h6" color="success.main" gutterBottom>
                            {selectedCountryData?.currencyCode || 'USD'} {formatPrice(pricePerMonth)}/month
                          </Typography>
                          
                          {tier.discountType === 'percentage' && tier.percentageDiscount > 0 && (
                            <Typography variant="caption" color="success.main">
                              ({tier.percentageDiscount}% discount applied)
                            </Typography>
                          )}
                          
                          <Typography variant="caption" color="textSecondary" display="block">
                            For {selectedDurationData ? getDurationLabel(selectedDurationData) : ''}
                          </Typography>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="subtitle2" gutterBottom>
                            Core Features: {tier.categoryFeatures?.length || 0}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Premium Add-ons: {tier.additionalFeatures?.length || 0}
                          </Typography>
                        </MDBox>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {(!selectedDuration || !selectedCountry) && activeTab > 0 && (
              <Box py={4} textAlign="center">
                <Typography variant="h6" color="textSecondary">
                  Please select duration and country to proceed
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setActiveTab(0)}
                  sx={{ mt: 2, color: "#fff" }}
                >
                  Select Duration & Country
                </Button>
              </Box>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default EditValuePackTier;
